import React, { useRef, Fragment, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Description, Transition } from '@headlessui/react';
import { useGenerateSoleFromDDLMutation, useMergeSoleMutation } from 'app/createApi';
import { convertFlatToNestedFormat, convertNestedToFlatFormat } from '../utils/helperFunctions';
import { INestedConfigFormat } from '../utils/configEditorTypes';
import { parse, stringify } from 'yaml';

interface AddObjectsFromDDLModalProps {
  open: boolean;
  onClose: () => void;
  nestedSoleConfig: INestedConfigFormat | undefined;
  setNestedSoleConfig: (config: INestedConfigFormat) => void;
}

const AddObjectsFromDDLModal = (props: AddObjectsFromDDLModalProps): JSX.Element => {
  const cancelButtonRef = useRef(null);

  const [ddlText, setDdlText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [generateSoleFromDDL] = useGenerateSoleFromDDLMutation();
  const [mergeSole] = useMergeSoleMutation();

  const oneUpdateSoleConfigWithDdl = (): void => {
    if (ddlText.trim() === '') {
      setShowErrorMessage(true);
      setErrorMessage('Please enter DDL text');
      return;
    }

    setIsLoading(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    generateSoleFromDDL({ ddl: ddlText })
      .unwrap()
      .then((result) => {
        const flatConfig =
          props.nestedSoleConfig !== undefined ? convertNestedToFlatFormat(props.nestedSoleConfig) : [];
        const currentSoleString = stringify(flatConfig);
        mergeSole({ currentSole: currentSoleString, addToSole: result, forceReplace: true })
          .unwrap()
          .then((mergeResult) => {
            console.log('Successfully merged SOLE configuration:', mergeResult);

            // Update the nested config with the merged result
            const parsedFlatConfig = parse(mergeResult.sole);
            const updatedNestedConfig = convertFlatToNestedFormat(parsedFlatConfig);
            props.setNestedSoleConfig(updatedNestedConfig);

            setShowSuccessMessage(true);
            setErrorMessage('');
            // Auto-close modal after 2 seconds
            setTimeout(() => {
              onClose();
            }, 2000);
          })
          .catch((error: unknown) => {
            setShowErrorMessage(true);
            const errorData = error as { data?: { message?: string } };
            setErrorMessage(errorData?.data?.message ?? 'Failed to merge SOLE configuration');
            console.error('Error merging SOLE configuration:', error);
          });
      })
      .catch((error: unknown) => {
        setShowErrorMessage(true);
        const errorData = error as { data?: { message?: string } };
        setErrorMessage(errorData?.data?.message ?? 'Failed to generate objects from DDL');
        console.error('Error generating SOLE from DDL:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClose = (): void => {
    props.onClose();
    setDdlText('');
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setIsLoading(false);
  };

  const isGenerateDisabled = ddlText.trim() === '' || isLoading;

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="w-[32rem] relative transform overflow-visible rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6">
                <div className="text-left">
                  <DialogTitle as="h3" className="text-xl text-center font-semibold leading-6 text-gray-900">
                    Add Objects from DDL
                  </DialogTitle>
                  <Description className="mb-4 text-center text-sm text-gray-600">
                    Paste your DDL (Data Definition Language) statements to automatically generate Snowflake object
                    configurations.
                  </Description>

                  <div className="w-full">
                    <div className="mt-2">
                      <label htmlFor="ddl-textarea" className="block text-sm font-medium leading-6 text-gray-900">
                        DDL Statements
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="ddl-textarea"
                          name="ddl"
                          rows={12}
                          value={ddlText}
                          onChange={(e) => setDdlText(e.target.value)}
                          className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {showSuccessMessage && (
                      <div className="mt-4 py-1 px-1.5 text-sm w-full rounded bg-green-50 border-2 border-green-500 text-green-600 flex items-center justify-center">
                        Objects generated successfully and added to the configuration!
                      </div>
                    )}

                    {showErrorMessage && errorMessage !== '' && (
                      <div className="mt-4 py-1 px-1.5 text-sm w-full rounded bg-red-50 border-2 border-red-500 text-red-600 flex items-center justify-center">
                        {errorMessage}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="flex-1 mt-3 inline-flex w-full justify-center rounded-md bg-dataops-secondary-blue px-3 py-2 text-sm font-semibold text-gray-50 shadow-sm hover:bg-hover-secondary-blue sm:mt-0 sm:w-auto disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={() => {
                      if (showSuccessMessage) {
                        onClose();
                      } else {
                        oneUpdateSoleConfigWithDdl();
                      }
                    }}
                    disabled={isGenerateDisabled}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Generating...
                      </>
                    ) : showSuccessMessage ? (
                      'Close'
                    ) : (
                      'Generate Objects'
                    )}
                  </button>
                  <button
                    type="button"
                    className="flex-1 mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:cursor-not-allowed"
                    ref={cancelButtonRef}
                    onClick={() => onClose()}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddObjectsFromDDLModal;
